// Minimal public-suffix coverage. Extend as needed.
const PUBLIC_SUFFIXES = new Set([
  // single-label common TLDs (add more as needed)
  'com','org','net','edu','gov','mil','int','io','ai','app','dev','uk','au','nz','jp','cn','br','mx','tr','pl','sg','id','il','ke','za','kr','in','us','de','fr','es','it','nl','se','no','fi','dk','ch','cz','sk','ro','gr','pt','ie','ru','hk','tw','vn','my','ph','th','sa','ae','qa','kw','ca',
  // multi-label public suffixes (examples; extend freely)
  'co.uk','ac.uk','gov.uk','org.uk','net.uk',
  'com.au','net.au','org.au','edu.au','gov.au',
  'co.nz','org.nz','gov.nz','net.nz',
  'co.jp','ne.jp','or.jp','ac.jp','go.jp',
  'com.cn','net.cn','org.cn','gov.cn','edu.cn',
  'com.br','net.br','org.br','gov.br',
  'com.sg','com.mx','com.tr','com.pl','com.es','com.fr','co.id','co.il','co.ke','co.za','com.ar','com.cl','com.pe'
]);

function isIPv4(host) {
  if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return false;
  return host.split('.').every(p => {
    const n = Number(p);
    return p.length > 0 && n >= 0 && n <= 255;
  });
}

function isLikelyBareIPv6(s) {
  // Has colon(s) but not a URL scheme like "http:"
  return s.includes(':') && !/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(s);
}

function normalizeUrlish(input) {
  let s = typeof input === 'string' ? input.trim() : String(input);

  // Strip wrappers that embed a URL
  s = s.replace(/^blob:|^view-source:|^filesystem:/i, '');

  // mailto: extract the domain after '@'
  if (/^mailto:/i.test(s)) {
    const after = s.slice(7);
    const at = after.lastIndexOf('@');
    if (at !== -1) s = 'http://' + after.slice(at + 1);
  }

  // Non-host schemes
  if (/^(data|tel|urn|file):/i.test(s)) return null;

  // Protocol-relative -> assume http
  if (s.startsWith('//')) s = 'http:' + s;

  // Bare IPv6 like "::1" -> bracket it
  if (isLikelyBareIPv6(s)) s = 'http://[' + s + ']';

  // If still no scheme, assume http
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(s)) s = 'http://' + s;

  try {
    return new URL(s);
  } catch {
    return null;
  }
}

function longestPublicSuffix(hostname) {
  const labels = hostname.toLowerCase().split('.').filter(Boolean);
  // Try longest -> shortest candidate in our set
  for (let i = 1; i <= labels.length; i++) {
    const cand = labels.slice(-i).join('.');
    if (PUBLIC_SUFFIXES.has(cand)) return cand;
  }
  // Fallback: treat rightmost label as suffix (even if not in our set)
  return labels.length ? labels.at(-1) : null;
}

/**
 * Returns eTLD+1 (e.g. "example.co.uk") or an object describing what it is
 * when no registrable domain exists.
 */
export function getRegistrableDomain(input) {
  const url = normalizeUrlish(input);
  if (!url) return { raw: String(input), type: 'non-host' };

  const hostname = url.hostname.toLowerCase();

  if (hostname === 'localhost') return { raw: hostname, type: 'localhost' };
  if (isIPv4(hostname) || hostname.includes(':')) return { raw: hostname, type: 'ip' };

  const labels = hostname.split('.').filter(Boolean);
  if (labels.length < 2) return { raw: hostname, type: 'dotless' };

  const publicSuffix = longestPublicSuffix(hostname); // e.g., "co.uk" or "com"
  const psLabels = publicSuffix ? publicSuffix.split('.').length : 1;

  if (labels.length <= psLabels) {
    // Host equals the suffix (e.g., "co.uk")
    return { raw: hostname, type: 'suffix-only' };
  }

  // eTLD+1 = one label left of the suffix + the suffix
  return labels.slice(-(psLabels + 1)).join('.');
}

// Basic brand-ification for unknown domains: split on dashes,
// title-case parts, preserve short all-caps (e.g., "API").
function prettifyLabel(label) {
  return label
    .split(/[-_]/g)
    .map(part => {
      if (part.length <= 4 && /^[a-z0-9]+$/i.test(part) && part.toUpperCase() === part) return part; // ALLCAPS 2–4 chars
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Derive a user-facing serviceName from any URL-ish input.
 * - Uses your getRegistrableDomain to get eTLD+1 (example.com, example.com.au)
 * - Checks a map for perfect branding
 * - Falls back to prettified SLD (leftmost label of eTLD+1)
 */
export function getServiceNameFromUrl(input) {
  const reg = getRegistrableDomain(input);

  // Handle non-registrable / special cases from your helper
  if (typeof reg !== 'string') {
    // reg is like { raw, type }
    // You can choose how to label these; here are reasonable defaults:
    if (reg.type === 'localhost') return 'Localhost';
    if (reg.type === 'ip') return reg.raw;
    // dotless, suffix-only, non-host -> no good service name
    return null;
  }

  // reg is eTLD+1 (e.g., "example.com", "example.com.au")
  const domain = reg.toLowerCase();

  // 2) Fallback: prettify the SLD (leftmost label of eTLD+1)
  //    "example.com"     -> "Example"
  //    "my-bank.com.au"  -> "My Bank"
  const sld = domain.split('.')[0];
  return prettifyLabel(sld);
}
